import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useT } from "@/i18n";
import { LOCALES, LOCALE_META, pathForLocale } from "@/i18n/locales";
import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";

import { useReferralCapture } from "@/hooks/useReferral";
import { useOutboundTagging } from "@/hooks/useOutboundTagging";
import { MaintenanceGate } from "@/components/maintenance-gate";
import { AnalyticsProvider } from "@/components/analytics-provider";
import { AdSenseProvider } from "@/components/ad-slot";
// Chrome that nobody needs during the first paint. Loading it after the page
// goes idle keeps it (and sonner) out of the hydration critical path, which is
// what mobile Lighthouse measures as Total Blocking Time.
const CookieConsent = lazy(() =>
  import("@/components/cookie-consent").then((m) => ({ default: m.CookieConsent })),
);
const LowHeatDialog = lazy(() =>
  import("@/components/low-heat-dialog").then((m) => ({ default: m.LowHeatDialog })),
);
const MessengerFab = lazy(() =>
  import("@/components/messenger-fab").then((m) => ({ default: m.MessengerFab })),
);
const Toaster = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })),
);

/** Mounts deferred chrome once the browser is idle after hydration. */
function DeferredChrome() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const idle =
      window.requestIdleCallback?.(() => setReady(true)) ??
      window.setTimeout(() => setReady(true), 1500);
    return () => {
      window.cancelIdleCallback?.(idle as number);
      window.clearTimeout(idle as number);
    };
  }, []);

  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <MessengerFab />
      <CookieConsent />
      <LowHeatDialog />
      <Toaster />
    </Suspense>
  );
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="rank-numeral text-7xl text-primary">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          This page isn't on the board.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "google-adsense-account", content: "ca-pub-8251395968499153" },
      {
        name: "google-site-verification",
        content: "EyPliyl-8nAPUOSNJ9ZJ2cGKS0waNf-VV8I2MewxgpA",
      },
      { title: "BeHot - Where anything can compete for attention" },
      {
        name: "description",
        content:
          "Put your song, gig, profile, brand, startup, app, product, business, event or idea on the live charts. List it, boost it, climb, get seen.",
      },
      { name: "author", content: "HRN Innovation Technologies Ltd" },
      { name: "application-name", content: "BeHot.Now" },
      { name: "apple-mobile-web-app-title", content: "BeHot.Now" },
      { property: "og:site_name", content: "BeHot.Now" },
      { property: "og:title", content: "BeHot - Where anything can compete for attention" },
      {
        property: "og:description",
        content:
          "List it → Boost it → Climb → Get seen. More boosts, higher ranking, more people see you.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },

    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico", sizes: "any" },
      { rel: "shortcut icon", type: "image/x-icon", href: "/favicon.ico" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/brand/favicon-32x32.png" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/brand/favicon-16x16.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/brand/favicon-192x192.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/brand/favicon-180x180.png" },

      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/LDIoaomQNQcsA88c7O9yZ4KMCoOg4Ko20yw.woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/V8mDoQDjQSkFtoMM3T6r8E7mPbF4Cw.woff2",
        crossOrigin: "anonymous",
      },


      {
        rel: "alternate",
        type: "text/plain",
        href: "https://behot.now/llms.txt",
        title: "BeHot.Now — plain-text summary for AI assistants",
      },
      { rel: "sitemap", type: "application/xml", href: "https://behot.now/sitemap-index.xml" },

    ],
    // Sitewide entity graph. Page-level schemas (Article, Product,
    // ProfilePage, BreadcrumbList) are added by each route's own head().
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://behot.now/#organization",
              name: "BeHot.Now",
              url: "https://behot.now",
              logo: "https://behot.now/brand/icon-192.png",
              description:
                "BeHot.Now is an attention marketplace with live paid rankings for creators, artists, startups, apps, brands and products.",
              parentOrganization: {
                "@type": "Organization",
                name: "HRN Innovation Technologies Ltd",
                url: "https://hrn.ae",
              },
              sameAs: ["https://x.com/BeHotNow2026", "https://facebook.com/BeHotNow"],
            },
            {
              "@type": "WebSite",
              "@id": "https://behot.now/#website",
              url: "https://behot.now",
              name: "BeHot.Now",
              publisher: { "@id": "https://behot.now/#organization" },
              inLanguage: ["en", "es", "de", "fr", "it", "ru", "pt-BR", "ar"],
            },
          ],
        }),
      },
    ],
  }),

  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const { meta } = useT();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  return (
    <html lang={meta.htmlLang} dir={meta.dir}>
      <head>
        <HeadContent />
        {LOCALES.map((code) => (
          <link
            key={code}
            rel="alternate"
            hrefLang={LOCALE_META[code].htmlLang}
            href={`https://behot.now${pathForLocale(pathname, code)}`}
          />
        ))}
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`https://behot.now${pathForLocale(pathname, "en")}`}
        />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function ReferralCapture() {
  useReferralCapture();
  useOutboundTagging();
  return null;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <ReferralCapture />
      <AnalyticsProvider />
      <AdSenseProvider />
      <div className="flex min-h-screen flex-col overflow-x-hidden">
        <SiteHeader />
        <main className="flex-1">
          {/* Required: nested routes render here. */}
          <MaintenanceGate>
            <Outlet />
          </MaintenanceGate>
        </main>
        <SiteFooter />
      </div>
      <DeferredChrome />
    </QueryClientProvider>
  );
}
